import { PWA_UPDATE_PROTOCOL } from './pwa-coordination';

export type UpdateActivationResult = 'accepted' | 'busy_clients' | 'clients_unverified' | 'other_windows' | 'verification_failed';

/** A bounded, opaque worker handshake. No authorization or business payload. */
export function requestUpdateActivation(worker: ServiceWorker, timeoutMs = 5_000): Promise<UpdateActivationResult> {
  if (typeof MessageChannel === 'undefined' || !Number.isFinite(timeoutMs) || timeoutMs <= 0 || timeoutMs > 10_000) return Promise.resolve('verification_failed');
  return new Promise((resolve) => {
    let channel: MessageChannel | undefined;
    let requestId: string;
    try { channel = new MessageChannel(); requestId = crypto.randomUUID(); }
    catch { channel?.port1.close(); channel?.port2.close(); resolve('verification_failed'); return; }
    const ports = channel;
    const expiresAt = Date.now() + timeoutMs;
    let finished = false;
    const complete = (result: UpdateActivationResult) => {
      if (finished) return;
      finished = true;
      clearTimeout(timer);
      ports.port1.close();
      ports.port2.close();
      resolve(result);
    };
    const timer = setTimeout(() => complete('verification_failed'), timeoutMs);
    ports.port1.onmessage = (event: MessageEvent<unknown>) => {
      const reply = event.data;
      if (Date.now() >= expiresAt) { complete('verification_failed'); return; }
      if (!reply || typeof reply !== 'object' || !('type' in reply) || reply.type !== 'EVENT_UPDATE_RESULT'
        || !('requestId' in reply) || reply.requestId !== requestId || !('result' in reply)
        || !['accepted', 'busy_clients', 'clients_unverified', 'other_windows', 'verification_failed'].includes(String(reply.result))) return;
      // A legacy refusal is harmless and remains explainable. Unversioned
      // acceptance cannot prove the new multi-window readiness contract.
      if (!('protocol' in reply) || reply.protocol !== PWA_UPDATE_PROTOCOL) {
        if (reply.result === 'other_windows' && !('protocol' in reply)) complete('other_windows');
        return;
      }
      complete(reply.result as UpdateActivationResult);
    };
    ports.port1.start();
    try {
      worker.postMessage({ type: 'ACTIVATE_EVENT_UPDATE_V1', protocol: PWA_UPDATE_PROTOCOL, requestId, expiresAt }, [ports.port2]);
    } catch { complete('verification_failed'); }
  });
}
